import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { UserRole } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  roles?: UserRole[]
  redirectTo?: string
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles,
  redirectTo = '/login'
}) => {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Checking authentication..." />
      </div>
    )
  }

  // If not authenticated, redirect to appropriate login page based on the route
  if (!isAuthenticated || !user) {
    // Determine the correct login page based on the current route
    // For admin routes (including reports), redirect to admin login
    let loginRedirect = redirectTo
    if (location.pathname.includes('/admin') || 
        location.pathname.includes('/nfcrwacontrol') || 
        location.pathname.includes('/reports') ||
        location.pathname.includes('/analytics')) {
      loginRedirect = '/nfcrwacontrol'
    } else if (location.pathname.includes('/staff') || location.pathname.includes('/department')) {
      loginRedirect = '/user-login'
    } else if (location.pathname.includes('/member')) {
      loginRedirect = '/login'
    }
    
    return <Navigate to={loginRedirect} state={{ from: location }} replace />
  }

  // Check role-based access
  if (roles && roles.length > 0) {
    if (!user || !roles.includes(user.role)) {
      // User doesn't have required role, redirect to unauthorized page
      return <Navigate to="/unauthorized" replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
